<template>
  <div class="gallery-header">
    <!-- Tabs and Pagination -->
    <div class="gallery-header__first-row">
      <div class="gallery-header__start">
        <div class="gallery-tabs">
          <button
            v-if="seqCount > 0"
            class="gallery-tab"
            :class="{ 'gallery-tab--active': selectedTab === 'sequences' }"
            @click="updateTab('sequences')"
          >
            Sequences ({{ seqCount }})
          </button>
          <button
            class="gallery-tab"
            :class="{ 'gallery-tab--active': selectedTab === 'images' }"
            @click="updateTab('images')"
          >
            Images ({{ imageCount }})
          </button>
        </div>
        <div class="gallery-actions__pagination" v-if="selectedTab === 'images'">
          <PageControl
            :modelValue="currentPage"
            @update:modelValue="updatePage"
            :totalPages="totalPages"
          />
        </div>
      </div>

      <div class="gallery-header__end">
        <!-- Sequence Actions -->
        <div v-if="selectedTab === 'sequences'" class="gallery-header__button-group">
          <Tags
            v-if="currentDataset?.id && !props.isPublic"
            :datasetId="currentDataset.id"
            :currentTag="currentTagValue"
            :tagInfo="currentTagInfo"
            @click="$emit('toggle-tags')"
            @action="handleTagsAction"
          />
          <GalleryActionsDropdown
            :mode="'sequences'"
            :dataset="currentDataset"
            :isPublic="isPublic"
            :selectedImages="selectedImages"
            :selectAll="selectAllFiles"
            :totalImageCount="seqCount"
            :pageImageCount="pageImageCount"
            @delete-images="$emit('delete-sequences')"
            @copy-image-modal="$emit('copy-modal')"
            @import-clicked="$emit('import-clicked')"
            @select-all-page="$emit('select-all-page')"
            @select-all="$emit('select-all')"
            @clear-selection="$emit('clear-selection')"
          />
        </div>
        
        <!-- Image Actions -->
        <div v-else class="gallery-header__button-group">
          <Tags
            v-if="currentDataset?.id && !props.isPublic"
            :datasetId="currentDataset.id"
            :currentTag="currentTagValue"
            :tagInfo="currentTagInfo"
            @click="$emit('toggle-tags')"
            @action="handleTagsAction"
          />
            <button class="button-light" @click="$emit('toggle-filter')">
                Filter
                <SVGIcon
                :iconName="'expand_more'"
                :width="'18px'"
                :height="'18px'"
                />
                <span v-if="activeFilterCount > 0" class="actions-badge">{{ activeFilterCount }}</span>
            </button>
          <DisplayAnnotationsFilter
            :modelValue="annotationDisplaySettings"
            @update:modelValue="$emit('update:annotationDisplaySettings', $event)"
            :annotationSets="annotationSets"
            :labels="currentLabels"
          />
          <DisplayImageDetails
            :modelValue="imageDisplaySettings"
            @update:modelValue="$emit('update:imageDisplaySettings', $event)"
            :hasSequences="hasSequences"
          />
          <GalleryActionsDropdown
            v-if="permissions?.dataset_write"
            :mode="'images'"
            :dataset="currentDataset"
            :isPublic="isPublic"
            :selectedImages="selectedImages"
            :selectAll="selectAllFiles"
            :totalImageCount="totalImageCount"
            :pageImageCount="pageImageCount"
            @remove-tag="$emit('remove-tag', $event)"
            @add-tag="$emit('add-tag', $event)"
            @delete-images="$emit('delete-images')"
            @refresh-dataset="$emit('refresh-dataset')"
            @copy-image-modal="$emit('copy-modal')"
            @import-clicked="$emit('import-clicked')"
            @select-all-page="$emit('select-all-page')"
            @select-all="$emit('select-all')"
            @clear-selection="$emit('clear-selection')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import PageControl from '@/components/DatasetComponent/GalleryComponent/PageControl.vue';
import GalleryActionsDropdown from '@/components/DatasetComponent/GalleryComponent/GalleryActionsDropdown.vue';
import DisplayAnnotationsFilter from '@/components/DatasetComponent/GalleryComponent/DisplayAnnotationsFilterV2.vue';
import DisplayImageDetails from '@/components/DatasetComponent/GalleryComponent/DisplayImageDetails.vue';
import Tags from '@/components/DatasetComponent/DatasetManagement/DatasetTags/Tags.vue';
import SVGIcon from '@/components/SVGIcon.vue';

const props = defineProps({
  seqCount: {
    type: Number,
    default: 0
  },
  imageCount: {
    type: Number,
    default: 0
  },
  currentPage: {
    type: Number,
    required: true
  },
  selectedTab: {
    type: String,
    default: ''
  },
  imagesPerPage: {
    type: Number,
    default: 40
  },
  permissions: {
    type: Object,
    default: null
  },
  imageDisplaySettings: {
    type: Array,
    default: () => []
  },
  annotationDisplaySettings: {
    type: Object,
    default: null
  },
  annotationSets: {
    type: Array,
    default: () => []
  },
  currentLabels: {
    type: Array,
    default: () => []
  },
  selectedImages: {
    type: Array,
    default: () => []
  },
  selectAllFiles: {
    type: Boolean,
    default: false
  },
  totalImageCount: {
    type: Number,
    default: 0
  },
  pageImageCount: {
    type: Number,
    default: 0
  },
  imageList: {
    type: Array,
    default: () => []
  },
  currentDataset: {
    type: Object,
    default: null
  },
  isPublic: {
    type: Boolean,
    default: false
  },
  hasSequences: {
    type: Boolean,
    default: false
  },
  activeFilterCount: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['update:currentPage', 'update:selectedTab', 'delete-sequences', 'delete-images', 'toggle-filter', 'toggle-tags', 'tag-this-version', 'restore-tag', 'update:imageDisplaySettings', 'update:annotationDisplaySettings', 'remove-tag', 'add-tag', 'refresh-dataset', 'copy-modal', 'import-clicked', 'select-all-page', 'select-all', 'clear-selection']);

const updatePage = (newPage) => {
  emit('update:currentPage', newPage);
};

const updateTab = (tab) => {
  emit('update:selectedTab', tab);
};

const handleTagsAction = (action) => {
  const actionType = typeof action === 'object' ? action?.type : action;

  if (action === 'info') {
    emit('toggle-tags');
    return;
  }
  if (actionType === 'tag-this-version') {
    emit('tag-this-version');
    return;
  }
  if (actionType === 'restore-tag') {
    emit('restore-tag', typeof action === 'object' ? action?.tagName : undefined);
  }
};

const currentTagValue = computed(() => props.currentDataset?.tag || props.currentDataset?.tad || '');
const currentTagInfo = computed(() => {
  const dataset = props.currentDataset || {};
  const tagPairs = Object.entries(dataset).filter(([key]) => key.toLowerCase().includes('tag'));

  if (!tagPairs.length) {
    return {
      tag: currentTagValue.value || '',
      tag_description: dataset.tag_description || dataset.tagDescription || ''
    };
  }

  return Object.fromEntries(tagPairs);
});

const totalPages = computed(() => {
  if (props.selectedTab === 'sequences') {
    return Math.ceil(props.seqCount / props.imagesPerPage);
  }
  return Math.ceil(props.imageCount / props.imagesPerPage);
});
</script>

<style lang="scss" scoped>
.gallery-header {
  display: flex;
  flex-direction: column;
  padding: 12px 20px 0 20px;
  border-bottom: 1px solid #c9c9c9;

  &__first-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  &__start {
    display: flex;
    align-items: center;
    flex: 1;
  }

  &__end {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  &__second-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
}

.gallery-tabs {
  display: flex;
  gap: 0;
  height: 100%;
  align-items: center;
}

.gallery-tab {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  border-bottom: 2px solid transparent;
  background: none;
  white-space: nowrap;
  color: var(--body-text-color-secondary, #666);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &--active {
    border-bottom: 2px solid var(--color-primary);
    color: var(--body-text-color, #000);
  }
}

.gallery-actions__pagination {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 30px;
}

.gallery-header__button-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gallery-filter-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  white-space: nowrap;
  color: var(--body-text-color, #000);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:active {
    opacity: 0.8;
  }
}

button.button-light {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;

  .actions-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 10px;
    background: #e53935;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
  }
}
</style>
